/**
 * Trip claims — receipts and overtime drivers and operator logins filed
 * against a trip, waiting for staff review. The dashboard's reading of the
 * API's TripClaimsService: the same counts, the same wording, one set of
 * status labels for every screen that shows a claim (the booking's Driver
 * Events tab, the banner in Order Details, the Trip claims page).
 */
import { isPendingOvertime } from './driverOvertime';
import { formatAmount } from './billingoDocuments';
import { formatDateTime } from './timezone';
import UserService from '../Services/UserService';

export const TRIP_CLAIMS_PATH = '/trip-claims';

/** Who may review claims: the receipt review's own permission, server-side too. */
export const TRIP_CLAIMS_MODULE = 'CanInteractWithOperatorsScreen';

export const canReviewTripClaims = () => UserService.getInstance()
  .canAccessModule(TRIP_CLAIMS_MODULE);

/** Fired after any review, so badges and lists re-read their counts. */
export const TRIP_CLAIMS_CHANGED = 'ph:trip-claims-changed';

export const announceTripClaimsChanged = () => {
  window.dispatchEvent(new Event(TRIP_CLAIMS_CHANGED));
};

const NOTHING = Object.freeze({ receipts: 0, overtime: 0, total: 0 });

/**
 * `{ receipts, overtime, total, blocking }` — the API's shape. `total` is
 * what waits for review; `blocking` is the subset that actually holds a
 * close or an invoice up. They differ for a claim filed before trip claims
 * started blocking (the API's TRIP_CLAIMS_BLOCK_FROM): it is shown and
 * counted, so staff work through it, but it stops nothing.
 */
export const NO_TRIP_CLAIMS = Object.freeze({ ...NOTHING, blocking: NOTHING });

/** A receipt waiting for review — the expense's own status for it. */
export const isPendingReceipt = (expense) => expense?.status === 'submitted';

/** `{ receipts, overtime, total }` from a booking's receipts and overtime entries. */
export const countTripClaims = (expenses = [], overtime = []) => {
  const receipts = (expenses || []).filter(isPendingReceipt).length;
  const pendingOvertime = (overtime || []).filter(isPendingOvertime).length;
  return { receipts, overtime: pendingOvertime, total: receipts + pendingOvertime };
};

/** The counts the Orders list carries on each booking (`trip_claims`). */
export const tripClaimsOf = (order) => order?.trip_claims || NO_TRIP_CLAIMS;

/**
 * The claims on a booking that actually block it. Falls back to everything
 * waiting when the API did not say (an older response), which errs towards
 * warning rather than towards a surprise 409.
 */
export const blockingClaimsOf = (order) => {
  const counts = tripClaimsOf(order);
  return counts.blocking || counts;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "2 trip claims waiting for review: 1 receipt, 1 overtime" — the API's wording. */
export const tripClaimsMessage = (counts) => {
  const parts = [
    counts?.receipts ? plural(counts.receipts, 'receipt', 'receipts') : '',
    counts?.overtime ? `${counts.overtime} overtime` : '',
  ].filter(Boolean);
  return `${plural(counts?.total || 0, 'trip claim', 'trip claims')} waiting for review: ${parts.join(', ')}`;
};

/** "2 to review" — the row badge. */
export const tripClaimsBadgeText = (counts) => `${counts?.total || 0} to review`;

/** The Trip claims page, narrowed to one booking. */
export const tripClaimsLink = (bookingId) => (
  bookingId ? `${TRIP_CLAIMS_PATH}?booking=${encodeURIComponent(bookingId)}` : TRIP_CLAIMS_PATH
);

/** A booking's Driver Events tab in Order Details. */
export const driverEventsLink = (bookingId) => (
  `/orders?booking=${encodeURIComponent(bookingId)}&tab=driver-events`
);

/**
 * One status wording for receipts and overtime alike. A receipt's
 * 'submitted' and an overtime entry's 'pending' are the same thing.
 */
export const CLAIM_STATUS_META = {
  pending: { label: 'Pending review', bg: 'warning', text: 'dark' },
  approved: { label: 'Approved', bg: 'success' },
  rejected: { label: 'Rejected', bg: 'danger' },
  running: { label: 'Still running', bg: 'secondary' },
};

export const claimStatusMeta = (status) => {
  if (status === 'submitted') return CLAIM_STATUS_META.pending;
  return CLAIM_STATUS_META[status] || { label: status || 'Unknown', bg: 'secondary' };
};

/** 'driver' or 'operator' — who filed the claim. */
const submitterKind = (claim) => claim?.submitted_by?.kind || (claim?.driver_id ? 'driver' : 'operator');

/**
 * Who filed a claim, from the API's `submitted_by`: "Driver: Peter Kovács
 * (Own Fleet Kft)", or — an operator login files with no driver —
 * "Operator: Own Fleet Kft". Never blank: a name the API could not read
 * says so. Receipts and overtime are named the same way.
 */
export function claimSubmitterLabel(claim) {
  const by = claim?.submitted_by;
  if (submitterKind(claim) === 'driver') {
    const driver = `Driver: ${by?.name || 'name unavailable'}`;
    return claim?.operator_name ? `${driver} (${claim.operator_name})` : driver;
  }
  return `Operator: ${by?.name || claim?.operator_name || 'name unavailable'}`;
}

/* --- what the photo said, as the dispatcher reads it -------------------- */
/**
 * THE RECEIPT SCAN BADGE — one badge per receipt row, and the sentence under
 * it. The row's only reading of `expense.scan`, which the API computes.
 *
 * TWO RULES, AND THEY ARE THE WHOLE DESIGN.
 *
 * 1. THE BADGE NAMES THE FIELD, NEVER THAT A MODEL RAN. "Photo says 4 500 Ft",
 *    not "AI scanned". A dispatcher with sixty rows waiting has to be able to
 *    ACT on the badge without opening anything, and "AI scanned" tells them
 *    only that they still have to look. The provenance (which model, when)
 *    belongs in the comparison panel, where somebody has already decided to
 *    look closely — not on the row.
 *
 * 2. THE THREE UNSCANNED STATES ARE DIFFERENT BADGES. If the absence of a
 *    badge could mean either "the photo defeated it" or "this row predates
 *    the feature", the column is noise inside a week and the dispatcher stops
 *    reading it. So an unreadable photo, a scan that never ran and a receipt
 *    filed before scanning existed each say so in their own words. The DRIVER
 *    is shown no such distinction (ExpenseSheet treats a failed scan exactly
 *    like a bad photo) because a driver cannot act on it; a dispatcher can —
 *    "check the photo yourself" is wrong advice for a row whose photo is fine
 *    and whose scan simply never happened.
 *
 * NOTHING HERE IS A POLICY DECISION. The disagreement, the date-outside-trip
 * flag and the days between are all the API's, already computed, and this
 * function only words them. A second copy of "how far outside the trip is too
 * far" in the browser is a second thing to change when the rule changes, and
 * the two would disagree the first time only one of them was changed.
 *
 * TWO ENDPOINTS HAVE TO CARRY `scan`, not one. The row is rendered from
 * `GET /operators/trip-claims` (the Trip claims page, inside `claim.expense`)
 * AND from `GET /operators/portal-expenses?bookingId=` (the booking's Driver
 * Events tab and the Order Details banner, through DriverExpensesService).
 * If only the first one carries it, two of the three surfaces show no badge
 * and look exactly like an API that has not shipped yet.
 *
 * THE FIELD NAMES BELOW WERE AGREED, NOT OBSERVED — the API half of receipt
 * scanning was built in parallel with this, so if a name does not match, this
 * function is the ONE place to fix it and no caller has to change. An expense
 * with no `scan` at all (an API that does not send the view yet) answers null
 * and the row renders as it always did.
 *
 *   expense.scan = {
 *     state: 'read' | 'unreadable' | 'unavailable' | 'predates_feature',
 *     read: { total, currency, occurred_at, merchant, total_source_line },
 *     amount_mismatch: { photo_total, typed_total, currency } | null,
 *     date_outside_trip: { days, direction: 'before' | 'after' } | null,
 *     duplicate_of: { expense_id, created_at } | null,
 *     scanning_from: <ISO date scanning started, for predates_feature>,
 *     model, scanned_at,
 *   }
 */
/**
 * "Receipt dated 3 Nov, 14:32 · OMV Budapest Hungária" — the sentence under a
 * scanned row, in the same register as `claimSubmitterLabel` above it.
 *
 * Every part is dropped when the scan did not read it, and the trip's own
 * date is NOT repeated here: `ClaimTrip` already renders it on the screens
 * that have one, and a row that said "trip 3 Nov" twice was the first thing
 * anybody noticed. The comparison with the trip is what the badge is for.
 */
// '3 Nov, 14:32' / '3 Nov' — a receipt's own date next to a trip date, read
// in Budapest time like every other date on these screens. Short on purpose:
// it sits inline in a sentence, not in a column.
const SCAN_DATE_TIME = 'D MMM, HH:mm';
const SCAN_DATE = 'D MMM';

function scanReadDetail(scan) {
  const read = scan?.read;
  if (!read) return '';
  const when = formatDateTime(read.occurred_at, SCAN_DATE_TIME);
  return [
    when ? `Receipt dated ${when}` : '',
    read.merchant || '',
  ].filter(Boolean).join(' · ');
}

const RECEIPT_SCAN_UNSCANNED = {
  unreadable: {
    key: 'unreadable',
    label: 'Not scanned — photo unreadable',
    icon: 'fa-eye-slash',
    detail: 'The driver sent it anyway. No date, place or total read — check the photo yourself.',
  },
  unavailable: {
    key: 'unavailable',
    label: 'Not scanned — the scan never ran',
    icon: 'fa-ban',
    detail: 'Nothing was read off this photo because the scan itself failed. The photo may be perfectly good.',
  },
};

/** `{ label, bg, text, icon, detail }` for a receipt's scan, or null. */
export function receiptScanBadge(expense) {
  const scan = expense?.scan;
  if (!scan) return null;

  const grey = { bg: 'light', text: 'dark' };

  // Most actionable first: a receipt that is BOTH a duplicate and a
  // disagreement is a duplicate, because deciding that question first can
  // make the other one moot.
  if (scan.duplicate_of) {
    return {
      key: 'duplicate',
      label: 'Possible duplicate',
      bg: 'info',
      text: 'dark',
      icon: 'fa-clone',
      detail: `The same photo as the receipt filed ${formatDateTime(scan.duplicate_of.created_at, SCAN_DATE_TIME) || 'earlier'}.`,
    };
  }

  if (scan.amount_mismatch) {
    const { photo_total: photoTotal, currency } = scan.amount_mismatch;
    return {
      key: 'amount_mismatch',
      label: `Photo says ${formatAmount(photoTotal, currency || expense.currency)}`,
      bg: 'warning',
      text: 'dark',
      icon: 'fa-triangle-exclamation',
      detail: scanReadDetail(scan),
    };
  }

  if (scan.date_outside_trip) {
    const { days, hours, direction } = scan.date_outside_trip;
    // HOURS WHEN WE HAVE THEM, and they are only sent when the receipt
    // actually printed a time — a day-only receipt cannot be placed more
    // precisely than its day, so it keeps the day wording.
    //
    // This distinction is the whole fix. A receipt 29 hours before the trip
    // used to read "Dated 1 day before the trip" (prod 2026-11-03,
    // PU_DASH_1933042: it in fact read "Scan agrees", because a 1-day gap sat
    // inside a 1-day window). "1 day" for 29 hours is a sentence that sounds
    // like it is INSIDE the tolerance it just failed, which is exactly why
    // the API measures in hours now and why the number it sends must be the
    // number shown.
    //
    // Above 48h hours stop being readable — "73 hours" is arithmetic, not
    // information — so it falls back to days there.
    let span;
    if (Number.isFinite(hours) && hours < 48) {
      span = hours === 1 ? '1 hour' : `${hours} hours`;
    } else {
      span = days === 1 ? '1 day' : `${days} days`;
    }
    return {
      key: 'date_outside_trip',
      label: `Dated ${span} ${direction === 'after' ? 'after' : 'before'} the trip`,
      bg: 'warning',
      text: 'dark',
      icon: 'fa-clock',
      detail: scanReadDetail(scan),
    };
  }

  if (scan.state === 'read') {
    return {
      key: 'agrees',
      label: 'Scan agrees',
      bg: 'success',
      icon: 'fa-check',
      detail: scanReadDetail(scan),
    };
  }

  if (scan.state === 'predates_feature') {
    const from = formatDateTime(scan.scanning_from, SCAN_DATE);
    return {
      key: 'predates_feature',
      ...grey,
      label: from ? `Not scanned — filed before ${from}` : 'Not scanned — filed before scanning existed',
      icon: 'fa-clock-rotate-left',
      detail: 'Sent before scanning existed. Nothing is wrong with it.',
    };
  }

  const unscanned = RECEIPT_SCAN_UNSCANNED[scan.state];
  return unscanned ? { ...unscanned, ...grey } : null;
}

/**
 * THE description a receipt's ledger line carries — the dashboard's mirror of
 * the API's `expenseLineDescription`, so the one-action approval and the
 * by-hand "Create line item" produce the SAME string.
 *
 * It ends with the receipt's own id, and that is load-bearing rather than
 * decorative. The ledger refuses a line whose kind, three amounts, currency,
 * operator, role and description all match one already on the booking — and
 * it does not look at metadata. Without something receipt-specific in the
 * description, two genuinely different 800 Ft parking receipts for the same
 * car on the same booking are indistinguishable and the second is refused;
 * with it, a repeated approval is still refused and two real receipts are not.
 *
 * The id is carried WHOLE. The description is the only field the guard can
 * tell receipts apart by, so it has to be exactly unique — a short tail of an
 * ObjectId is readable and not trustworthy enough to refuse money with.
 */
export function expenseAdjustmentDescription(expense) {
  const who = submitterKind(expense);
  const label = expense.type === 'parking' ? 'Parking' : `${who === 'driver' ? 'Driver' : 'Operator'} expense`;
  const note = expense.note?.trim();
  const head = note ? `${label} — ${note}` : `${label} (${who} receipt)`;
  const id = expense._id ? String(expense._id) : ''; // eslint-disable-line no-underscore-dangle

  return id ? `${head} · receipt ${id}` : head;
}

/**
 * The 409 the API answers "Mark Completed" (or a final Számla) with while
 * trip claims wait: `{ message, booking_id, trip_claims }`. null for any
 * other error.
 */
export const tripClaimsRefusalOf = (error) => {
  const body = error?.response?.status === 409 ? error.response.data : null;
  return body?.trip_claims ? {
    bookingId: body.booking_id,
    counts: body.trip_claims,
    message: body.message || tripClaimsMessage(body.trip_claims),
  } : null;
};

/** Fired when the API refuses to complete a booking; TripClaimsRefusalAlert shows it. */
export const TRIP_CLAIMS_REFUSED = 'ph:trip-claims-refused';

export const announceTripClaimsRefused = (order, refusal) => {
  window.dispatchEvent(new CustomEvent(TRIP_CLAIMS_REFUSED, {
    detail: {
      bookingId: refusal.bookingId || order?._id, // eslint-disable-line no-underscore-dangle
      reference: order?.booking_reference,
      counts: refusal.counts,
      message: refusal.message,
    },
  }));
};

/**
 * A finished trip — one a final Számla may be issued for. Mirrors the API's
 * FinalInvoiceGuard: a cancellation fee and a no-show charge are real money
 * and have always been invoiced, so it is not only `Completed`.
 */
export const FINISHED_BOOKING_STATUSES = ['Completed', 'Cancelled', 'Driver No-Show', 'Passenger No-Show'];

/**
 * An experience is exempt from the status rule: the nightly job never
 * completes one, so it is invoiced from `Confirmed`. It is NOT exempt from
 * the claims rule — a receipt can be filed against one.
 */
export const EXPERIENCE_BOOKING_TYPE = 'experience';

export const FINAL_INVOICE_REFUSAL = 'A final Számla needs a finished booking with nothing waiting for review';

/**
 * Why this ONE booking can't be finally invoiced yet, or '' when it can —
 * the dashboard's reading of the API's FinalInvoiceGuard, in the same words
 * the 409 uses. A booking whose status isn't known here is left to the
 * server rather than guessed at.
 */
export function finalInvoiceBlockerOf(booking) {
  const unfinished = Boolean(booking?.status)
    && !FINISHED_BOOKING_STATUSES.includes(booking.status)
    && booking.booking_type !== EXPERIENCE_BOOKING_TYPE;
  const blocking = blockingClaimsOf(booking);

  return [
    unfinished ? `the trip is not finished (${booking.status})` : '',
    blocking.total > 0 ? tripClaimsMessage(blocking) : '',
  ].filter(Boolean).join(', and ');
}

/**
 * THE list of bookings that block a final Számla, each with its reference
 * and its own reason.
 *
 * One function, every caller: the per-order invoicing panel, the review step
 * of the document flow, and the Invoicing screen's warning when a billing
 * period is chosen. A month-end Számla carries every booking of the period,
 * so "something is wrong somewhere" is useless — each of these has to be
 * nameable and linkable.
 */
export function finalInvoiceBlockers(bookings = []) {
  return (bookings || []).flatMap((booking) => {
    const reason = finalInvoiceBlockerOf(booking);

    return reason ? [{
      // eslint-disable-next-line no-underscore-dangle
      bookingId: booking._id,
      reference: booking.booking_reference,
      status: booking.status,
      counts: blockingClaimsOf(booking),
      reason,
    }] : [];
  });
}

/** How a blocked booking reads on one line: "PU_DASH_1: the trip is not finished (Confirmed)". */
export const finalInvoiceBlockerLabel = (blocker) => (
  `${blocker.reference || blocker.bookingId || 'Booking'}: ${blocker.reason}`
);

/**
 * Why a final Számla can't be issued for these bookings yet, or '' when it
 * can — one sentence, for a disabled button's tooltip. The bookings
 * themselves are named by `finalInvoiceBlockers`.
 */
export function finalInvoiceBlockedReason(bookings = []) {
  const blocked = finalInvoiceBlockers(bookings);

  if (!blocked.length) return '';

  if (blocked.length === 1) {
    return `${FINAL_INVOICE_REFUSAL} — ${finalInvoiceBlockerLabel(blocked[0])}`;
  }

  return `${FINAL_INVOICE_REFUSAL} — ${blocked.length} of these bookings are not ready: `
    + `${blocked.map((blocker) => blocker.reference || blocker.bookingId).join(', ')}`;
}
